import * as Sentry from '@sentry/react';
import ErrorAndBackButton from "./ErrorAndBackButton";
import { ApiError } from '../model/errors/errors';

function DisplayDebugInfo({error, debugEnabled}: {error: string, debugEnabled: boolean}) {
  if (debugEnabled) {
    return (
      <>
        <hr/>
        <strong>{"ERROR MESSAGE FOR DEBUGGING: "}</strong>
        <p>{ error }</p>
        <i>Please note this error page is typically displayed when hitting backend validations.</i>
        <hr/>
      </>
    );
  } else {
    return null;
  }
}

const getErrorMessageFromHistoryState = (historyState: any): {
  presentable: boolean,
  message: string,
} => {
  let error = historyState?.state?.error;
  if (!error) {
    // Note this error isn't displayed client-side, it was just an edge case I found during testing that crashes the app.
    error = "No error found in history state. This was likely caused by a user refreshing the Sorry page after getting kicked out of registration.";
  } else if (error.type) {
    return {
      presentable: true,
      message: ApiError.deserialize(error)!.toString(),
    };
  }

  let message;
  if (typeof error === 'string') {
    message = error;
  } else if (error?.errors) {
    let errorMessages: string[] = [];
    Object.keys(error.errors).forEach((key: string) => {
      let message = key + ': ' + error.errors[key];
      errorMessages.push(message);
    });

    message = errorMessages.join();
  } else {
    if (import.meta.env.VITE_APP_ENV !== 'prod') {
      console.log('Unknown error encountered: ');
      console.log(error);
    }

    message = String(error);
  }

  return {
    presentable: false,
    message
  };
}

/*
 * This component is the fallback component of the "Sorry"
 * chain of business related error handling.
 * It primarily parses the contents of the History state to show
 * the error depending on environment and also shoots off a Sentry alert
 * See: https://developer.mozilla.org/en-US/docs/Web/API/History/state
 */
const SorryFallback = ({ historyState }: any) => {
  const { presentable, message } = getErrorMessageFromHistoryState(historyState);
  const error = new Error("Unhandled Sorry error: " + message);
  Sentry.captureException(error); // TODO: should wrap this in useEffect() hook

  return (
    <>
      { presentable && <ErrorAndBackButton errorMessage={message} /> }
      { !presentable && <>
        <ErrorAndBackButton errorMessage="An unexpected error has occurred. The issue has been logged and we're looking into it." /> 
        <DisplayDebugInfo
          error={message}
          debugEnabled={false}
        />
      </> }
    </>
  );
}

export default SorryFallback;
